import { CommentStatus, type Comment } from '@/@types/comment'
import StickStickIcon from '@/assets/stick-stick.svg?react'
import { formatAsRelativeTime } from '@/utils/day'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import type { ReactNode } from 'react'
import ClickableStackAvatarGroup from '../ClickableStackAvatarGroup'
import ContentContainer from '../ContentContainer'
import ImageGridSection from '../ImageGridSection'
import TextContent from '../TextContent'
import CommentHeader from './CommentHeader'
import CommentHeaderTitle from './CommentHeaderTitle'
import styles from './CommentItem.module.css'

export interface CommentItemProps extends HTMLMotionProps<'div'> {
	actions?: ReactNode
	clickable?: boolean
	data: Comment
	isPostAuthor?: boolean
	noSuffixTitle?: boolean
	replies?: ReactNode
	showThreadLine?: boolean
}

export default function CommentItem({
	actions,
	clickable = false,
	data,
	isPostAuthor = false,
	noSuffixTitle = false,
	replies,
	showThreadLine = false,
	...props
}: CommentItemProps) {
	const { createdAt, isPinned } = data
	const isDeleted = data.deleted || data.status === CommentStatus.DELETED

	return (
		<AnimatePresence mode="popLayout">
			{!isDeleted && (
				<motion.div
					exit={{ opacity: 0 }}
					data-anonymous-comment-navigation={clickable || undefined}
					data-clickable-feedback={clickable || undefined}
					{...props}
				>
					{isPinned && (
						<div className={styles.pinnedLabel}>
							<StickStickIcon className={styles.pinnedIcon} color="currentColor" />
							置顶
						</div>
					)}
					<div className={styles.container}>
						<div className={styles.avatarContainer}>
							<ClickableStackAvatarGroup data={[data.user]} />

							{showThreadLine && (
								<div className={styles.threadLineContainer}>
									<div className={styles.threadLine} />
								</div>
							)}
						</div>

						<div>
							<CommentHeader
								title={
									<CommentHeaderTitle
										actions={actions}
										data={data}
										isPostAuthor={isPostAuthor}
										noSuffixTitle={noSuffixTitle}
									/>
								}
								desc={formatAsRelativeTime(createdAt)}
							/>

							<ContentContainer className={styles.contentContainer}>
								<TextContent content={data.content} urlsInText={data.urlsInText} />
								{data.pictures && (
									<ImageGridSection data={data.pictures} className={styles.pictureGrid} />
								)}
							</ContentContainer>

							{replies}
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
